/**
 * Preview mode of the WhatsApp inbox: the same three-pane layout over local sample rows (inbox-sample.ts). It is entered only by an
 * explicit click, is always badged, and never touches the API: no bff import, every action is a reducer step on component state.
 */
import { useReducer, useState } from 'react';
import { Heading, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { useFormat } from '../../../lib/format';
import '../_shared/customer.css';
import './inbox-preview.css';
import { BrandLogo, providerFromLabel } from '../../../shell/BrandLogo';
import { initialPreview, inPreviewTab, pick, PREVIEW_TABS, previewCounts, previewReducer, type PreviewTab, type SampleConversation } from './inbox-sample';

const QUAL_KEYS = ['intent', 'budget', 'timeframe', 'need', 'fit'] as const;
const SAMPLE_TEAM = ['Sample Agent A', 'Sample Agent B'];

export function InboxPreview({ onExit }: { onExit: () => void }) {
  const { t, locale } = useApp();
  const f = useFormat();
  const [state, dispatch] = useReducer(previewReducer, undefined, initialPreview);
  const [tab, setTab] = useState<PreviewTab>('open');
  const [active, setActive] = useState<string | null>(null);
  const counts = previewCounts(state.rows);
  const shown = state.rows.filter((r) => inPreviewTab(r, tab));
  const current = state.rows.find((r) => r.id === active) ?? null;
  return (
    <>
      <div className="cu-preview-banner" role="status" data-testid="inbox-preview-banner">
        <b>{t('whatsapp-inbox.preview.banner')}</b>
        <button type="button" className="cu-btn" data-variant="outline" onClick={onExit}>{t('whatsapp-inbox.preview.exit')}</button>
      </div>
      <div className="qs-inbox" data-preview="true">
        <div>
          <div className="cu-tabs" role="group" aria-label={t('whatsapp-inbox.pane.tabs')}>
            {PREVIEW_TABS.map((k) => <button key={k} type="button" aria-pressed={tab === k} onClick={() => setTab(k)}>{t(`whatsapp-inbox.pane.tab.${k}`)} <span aria-hidden="true">{f.number(counts[k])}</span></button>)}
          </div>
          <nav aria-label={t('shell.screens.inbox.conversations')}>
            {shown.length ? (
              <ul className="qs-inbox__list cu-convlist">
                {shown.map((r) => {
                  const name = pick(r.name, locale);
                  return (
                    <li key={r.id} data-unread={r.unread ? 'true' : undefined}>
                      <button type="button" className="qs-inbox__row" aria-pressed={active === r.id} aria-current={active === r.id ? 'true' : undefined} onClick={() => setActive(r.id)}>
                        <span className="qs-avatar" aria-hidden="true">{[...name][0]?.toUpperCase() ?? ''}</span>
                        <span><span className="cu-conv-name"><bdi>{name}</bdi></span>
                          <small>{t(`whatsapp-inbox.state.${r.state}`)} <span className="cu-sample-tag">{t('whatsapp-inbox.preview.tag')}</span></small></span>
                        <span className="cu-conv-time">{f.time(r.lastMessageAt)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : <Text tone="muted">{t('whatsapp-inbox.pane.tabEmpty')}</Text>}
          </nav>
        </div>
        <div className="cu-thread" aria-live="polite">
          {current ? <PreviewThread c={current} dispatch={dispatch} /> : <div className="qs-inbox__thread"><Text tone="muted">{t('shell.screens.inbox.pick')}</Text></div>}
        </div>
        <aside className="qs-inbox__lead" aria-label={t('whatsapp-inbox.pane.lead')}>
          {current ? <PreviewLead c={current} dispatch={dispatch} /> : <Text tone="muted">{t('whatsapp-inbox.pane.leadPick')}</Text>}
        </aside>
      </div>
    </>
  );
}

type Dispatch = (a: Parameters<typeof previewReducer>[1]) => void;
const nowIso = () => new Date().toISOString();

function PreviewThread({ c, dispatch }: { c: SampleConversation; dispatch: Dispatch }) {
  const { t, locale } = useApp();
  const f = useFormat();
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState('');
  const draft = c.draft;
  return (
    <>
      <div className="cu-thread__head"><Heading level={2}><bdi>{pick(c.name, locale)}</bdi></Heading><span className="cu-sample-tag">{t('whatsapp-inbox.preview.tag')}</span><span className="qs-status"><span className="qs-dot" data-tone={c.state === 'ai_active' ? 'accent' : c.state === 'human_active' ? 'good' : undefined} aria-hidden="true" />{t(`whatsapp-inbox.state.${c.state}`)}</span></div>
      <ol className="cu-thread__body">
        {c.messages.map((m) => <li key={m.id} className="qm-bubble" data-direction={m.direction}><bdi>{pick(m.text, locale)}</bdi> <small>{f.time(m.at)}</small></li>)}
      </ol>
      {draft ? (
        <div className="qs-draft" data-testid="inbox-draft">
          <span className="qs-micro">{t('whatsapp-inbox.draft.title')}</span>
          {editing ? <textarea className="cu-draft-edit" aria-label={t('whatsapp-inbox.draft.edit')} value={edit} onChange={(e) => setEdit(e.target.value)} rows={3} /> : <p><bdi>{pick(draft.body, locale)}</bdi></p>}
          <div className="qs-draft__actions">
            <button type="button" className="cu-btn" disabled={editing && !edit.trim()} onClick={() => { dispatch({ type: 'approve', id: c.id, at: nowIso(), ...(editing ? { body: edit.trim() } : {}) }); setEditing(false); }}>{t('whatsapp-inbox.pane.approveSend')}</button>
            {editing ? null : <button type="button" className="cu-btn" data-variant="outline" onClick={() => { setEditing(true); setEdit(pick(draft.body, locale)); }}>{t('whatsapp-inbox.pane.editDraft')}</button>}
            <button type="button" className="cu-btn" data-variant="outline" onClick={() => { dispatch({ type: 'deleteDraft', id: c.id }); setEditing(false); }}>{t('whatsapp-inbox.preview.deleteDraft')}</button>
          </div>
        </div>
      ) : null}
      <form className="cu-composer" onSubmit={(e) => { e.preventDefault(); const body = text.trim(); if (body) { dispatch({ type: 'send', id: c.id, body, at: nowIso() }); setText(''); } }}>
        <textarea aria-label={t('whatsapp-inbox.pane.compose')} placeholder={t('whatsapp-inbox.pane.compose')} value={text} maxLength={4096} rows={2} onChange={(e) => setText(e.target.value)} />
        <button type="submit" className="cu-btn" disabled={!text.trim()}>{t('whatsapp-inbox.actions.send')}</button>
      </form>
      <p className="cu-hint cu-preview-note">{t('whatsapp-inbox.preview.localOnly')}</p>
    </>
  );
}

function PreviewLead({ c, dispatch }: { c: SampleConversation; dispatch: Dispatch }) {
  const { t, locale } = useApp();
  const f = useFormat();
  const [assignee, setAssignee] = useState('');
  const captured = QUAL_KEYS.filter((k) => pick(c.qualification[k], locale)).length;
  return (
    <div className="cu-leadpanel">
      <Heading level={2}>{t('whatsapp-inbox.pane.lead')}</Heading>
      <div>
        <div className="qs-pulse__head"><span className="qs-micro">{t('whatsapp-inbox.pane.qualification')}</span><b>{f.number(captured)}/{f.number(QUAL_KEYS.length)}</b></div>
        <div className="cu-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((captured / QUAL_KEYS.length) * 100)} aria-label={t('whatsapp-inbox.pane.qualification')}><span style={{ inlineSize: `${(captured / QUAL_KEYS.length) * 100}%` }} /></div>
      </div>
      <ul className="qs-kv">
        {QUAL_KEYS.map((k) => { const v = pick(c.qualification[k], locale); return <li key={k}><span className="qs-kv__label">{t(`whatsapp-inbox.pane.field.${k}`)}</span><span className="qs-kv__value">{v ? <bdi>{v}</bdi> : <small>{t('whatsapp-inbox.pane.notCaptured')}</small>}</span></li>; })}
      </ul>
      <ul className="qs-kv">
        <li><span className="qs-kv__label">{t('whatsapp-inbox.pane.phone')}</span><span className="qs-kv__value" dir="ltr">{c.phoneMasked}</span></li>
        <li><span className="qs-kv__label">{t('whatsapp-inbox.pane.owner')}</span><span className="qs-kv__value" data-testid="preview-owner">{c.owner ? <bdi>{c.owner}</bdi> : t('whatsapp-inbox.pane.unassigned')}</span></li>
        <li><span className="qs-kv__label">{t('whatsapp-inbox.pane.source')}</span><span className="qs-kv__value">{providerFromLabel(c.source.en) ? <><BrandLogo provider={providerFromLabel(c.source.en)!} size={16} decorative /> </> : null}<bdi>{pick(c.source, locale)}</bdi></span></li>
        <li><span className="qs-kv__label">{t('whatsapp-inbox.pane.campaign')}</span><span className="qs-kv__value"><bdi>{pick(c.campaign, locale)}</bdi></span></li>
        <li><span className="qs-kv__label">{t('whatsapp-inbox.pane.tags')}</span><span className="qs-kv__value">{c.tags.map((g) => <span key={g.en} className="cu-sample-tag"><bdi>{pick(g, locale)}</bdi></span>)}</span></li>
      </ul>
      <div className="cu-btnrow">
        <button type="button" className="cu-btn" disabled={c.state === 'human_active'} onClick={() => dispatch({ type: 'takeover', id: c.id })}>{t('whatsapp-inbox.pane.takeOver')}</button>
        <button type="button" className="cu-btn" data-variant="outline" onClick={() => dispatch({ type: 'snooze', id: c.id, on: c.state !== 'snoozed' })}>{t(c.state === 'snoozed' ? 'whatsapp-inbox.preview.unsnooze' : 'whatsapp-inbox.preview.snooze')}</button>
      </div>
      <div className="cu-field">
        <label htmlFor="preview-assignee"><span>{t('whatsapp-inbox.pane.assignTeam')}</span></label>
        <select id="preview-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
          <option value="">{t('whatsapp-inbox.pane.choose')}</option>
          {SAMPLE_TEAM.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <button type="button" className="cu-btn" data-variant="outline" disabled={!assignee} onClick={() => { dispatch({ type: 'assign', id: c.id, owner: assignee }); setAssignee(''); }}>{t('whatsapp-inbox.pane.assign')}</button>
      </div>
      <button type="button" className="cu-btn" data-variant="outline" disabled={c.opportunity} onClick={() => dispatch({ type: 'createOpportunity', id: c.id })}>{t('whatsapp-inbox.pane.createOpp')}</button>
      {c.opportunity ? <p role="status" className="cu-hint">{t('whatsapp-inbox.preview.oppLocal')}</p> : null}
    </div>
  );
}
